import { 
  Bot,
  LayoutDashboard, 
  Users, 
  FileSpreadsheet, 
  Upload, 
  DollarSign, 
  Tag,
  PackagePlus,
  BookOpen,
  Phone,
  Briefcase,
  Timer,
  Sparkles,
  PieChart as PieChartIcon2,
  BarChart as BarChartIcon2,
  UserPlus,
  UserCog, 
  BarChart3, 
  Settings,
  LogOut,
  ChevronLeft,
  Menu,
  Receipt,
  ChevronDown,
  FileText,
  TrendingUp,
  PieChart,
  Award,
  History,
  UserCheck,
  Trash2,
  Building2,
  Wallet,
  Banknote,
  ShieldAlert,
  TrendingDown,
  Landmark,
  AlertTriangle,
  CalendarDays,
  Gift,
  Calculator,
  FileBarChart,
  UsersRound,
  ClipboardList,
  Activity,
  Clock,
  Target,
  Sliders,
  Trophy,
  User,
  Shield,
  Globe,
  Coins,
  Bell,
  Send,
  UtensilsCrossed,
  ShoppingCart,
  ChefHat,
  CreditCard,
  ClipboardCheck,
  ScrollText,
  Package,
  Camera,
  Lightbulb
} from 'lucide-react';
import { useState, useMemo, memo, useCallback, ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { UserRole } from '@/types';
import { useAllowedRoutes } from '@/hooks/useAllowedRoutes';
import medius360LogoWhite from '@/assets/medius360-logo-white.png';
import medius360LogoDark from '@/assets/medius360-logo-dark.png';
import { useTheme } from '@/contexts/ThemeContext';

import { PhoneMissed, CircleDollarSign, PhoneCall, Eye, Gauge, XCircle, Hourglass, ClipboardList as ClipboardListIcon } from 'lucide-react';

/* ───────────────────────────────────────────
   Sub-item / Direct-item types
   ─────────────────────────────────────────── */

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  allowedRoles: UserRole[];
}

interface SubItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

/* ═══════════════════════════════════════════
   2. SALES — sub-items
   ═══════════════════════════════════════════ */

const salesDirectItems: NavItem[] = [
  { title: 'Deals', href: '/deals', icon: DollarSign, allowedRoles: ['admin', 'supervisor', 'billing', 'agent'] },
  { title: 'Dialer', href: '/dialer', icon: PhoneCall, allowedRoles: ['admin', 'supervisor', 'agent'] },
];

const listsSubItems: SubItem[] = [
  { title: 'Import', href: '/lists/import', icon: Upload },
  { title: 'Import Advanced', href: '/lists/import-advanced', icon: Sparkles },
  { title: 'Statistics', href: '/lists/statistics', icon: PieChartIcon2 },
  { title: 'Statistics Advanced', href: '/lists/statistics-advanced', icon: BarChartIcon2 },
  { title: 'Assigner', href: '/lists/assigner', icon: UserPlus },
  { title: 'Lead Scraper', href: '/lists/scraper', icon: Globe },
];

/* ═══════════════════════════════════════════
   3. OPERATIONS — sub-items
   ═══════════════════════════════════════════ */

const workSubItemsAll: SubItem[] = [
  { title: 'Call Screen', href: '/work', icon: Phone },
  { title: 'Finance Board', href: '/work/finance-board', icon: Receipt },
  { title: 'Finance Analytics', href: '/work/finance-analytics', icon: Calculator },
];

const servicesSubItems: SubItem[] = [
  { title: 'Manage Services', href: '/services', icon: PackagePlus },
  { title: 'Service Catalog', href: '/services/catalog', icon: BookOpen },
];

/* ═══════════════════════════════════════════
   4. FINANCE — sub-items (grouped internally)
   ═══════════════════════════════════════════ */


const financeAccountingSubItems: SubItem[] = [
  { title: 'Predračuni', href: '/finance/proforma', icon: FileText },
  { title: 'Obaveze plaćanja', href: '/finance/payment-flow', icon: CalendarDays },
  { title: 'Country Cost Center', href: '/finance/country-costs', icon: Globe },
  { title: 'Investments', href: '/finance/investments', icon: TrendingUp },
];


/* ═══════════════════════════════════════════
   5. REPORT CENTER — sub-items
   ═══════════════════════════════════════════ */


const teamAgentsSubItems: SubItem[] = [
  { title: 'Teams', href: '/team-agents/teams', icon: UsersRound },
  { title: 'Agents', href: '/team-agents/agents', icon: Users },
  { title: 'Countries', href: '/team-agents/countries', icon: Globe },
  { title: 'Team Report', href: '/team-agents/team-report', icon: ClipboardList },
  { title: 'Agent Report', href: '/team-agents/agent-report', icon: BarChart3 },
  { title: 'Agent Activity', href: '/team-agents/activity', icon: Activity },
  { title: 'Work Hours', href: '/team-agents/work-hours', icon: Clock },
];

const teamBonusSubItems: SubItem[] = [
  { title: 'Daily Targets', href: '/team-bonus', icon: Target },
  { title: 'Bonus Rules', href: '/team-bonus/rules', icon: Sliders },
  { title: 'Bonus History', href: '/team-bonus/history', icon: History },
];

const salesReportsSubItems: SubItem[] = [
  { title: 'Default', href: '/reports', icon: FileText },
  { title: 'Dynamic Report', href: '/reports/dynamic', icon: TrendingUp },
  { title: 'Dynamic Chart', href: '/reports/chart', icon: PieChart },
  { title: 'Agent Bonus Report', href: '/reports/agent-bonus', icon: Award },
];

const legacyReportsSubItems: SubItem[] = [
  { title: 'Status History', href: '/reports/status-history', icon: History },
  { title: 'Personals', href: '/reports/personals', icon: UserCheck },
  { title: 'Trash/Cancel List', href: '/reports/trash-cancel', icon: Trash2 },
  { title: "Can't Call List", href: '/reports/cant-call', icon: PhoneMissed },
  { title: 'Paid List', href: '/reports/paid', icon: CircleDollarSign },
];


/* ═══════════════════════════════════════════
   6. SETTINGS — sub-items
   ═══════════════════════════════════════════ */

const settingsSubItemsAll: SubItem[] = [
  { title: 'My Profile', href: '/settings', icon: User },
  { title: 'Permissions', href: '/settings/permissions', icon: Shield },
  { title: 'Countries', href: '/settings/countries', icon: Globe },
  { title: 'Currencies', href: '/settings/currencies', icon: Coins },
  { title: 'Duplicate Manager', href: '/duplicate-manager', icon: Users },
];

const notificationsSubItems: SubItem[] = [
  { title: 'Inbox', href: '/notifications', icon: Bell },
  { title: 'Send Custom', href: '/notifications/send', icon: Send },
];

/* ═══════════════════════════════════════════
   KITCHEN — kept as internal/support module
   ═══════════════════════════════════════════ */

const kitchenSubItems: SubItem[] = [
  { title: 'Order', href: '/kitchen', icon: ShoppingCart },
  { title: 'My Orders', href: '/kitchen/my-orders', icon: ClipboardCheck },
  { title: 'Kitchen Manager', href: '/kitchen/manager', icon: ChefHat },
  { title: 'Kitchen Orders', href: '/kitchen/orders', icon: ClipboardList },
  { title: 'Balance Management', href: '/kitchen/balance', icon: CreditCard },
  { title: 'Transaction History', href: '/kitchen/transactions', icon: ScrollText },
  { title: 'Order History', href: '/kitchen/history', icon: History },
  { title: 'Items Report', href: '/kitchen/report', icon: Package },
];

/* ═══════════════════════════════════════════
   MAIN SIDEBAR COMPONENT
   ═══════════════════════════════════════════ */

interface AppSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export const AppSidebar = memo(function AppSidebar({ isOpen, onToggle }: AppSidebarProps) {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const { routes: allowedRoutes } = useAllowedRoutes();

  /* ─── Permission filtering helpers ─── */

  const filterByPermission = useMemo(() => {
    return (items: SubItem[]) => {
      if (!allowedRoutes) return items;
      return items.filter(sub => allowedRoutes.has(sub.href));
    };
  }, [allowedRoutes]);

  const filterDirectItems = useMemo(() => {
    return (items: NavItem[]) => {
      return items.filter(item => {
        if (allowedRoutes && !allowedRoutes.has(item.href)) return false;
        if (item.allowedRoles && user?.role && !item.allowedRoles.includes(user.role as UserRole)) return false;
        return true;
      });
    };
  }, [allowedRoutes, user?.role]);

  const showSection = (href: string) => !allowedRoutes || allowedRoutes.has(href);

  const showSectionOrSubs = useCallback((basePath: string) => {
    if (!allowedRoutes) return true;
    for (const route of allowedRoutes) {
      if (route === basePath || route.startsWith(basePath + '/')) return true;
    }
    return false;
  }, [allowedRoutes]);

  /* ─── Section visibility ─── */

  const sectionVisibility = useMemo(() => ({
    showFinanceOverview: showSectionOrSubs('/finance'),
    showReports: showSectionOrSubs('/reports'),
    showTeamAgents: showSectionOrSubs('/team-agents'),
    showLists: showSectionOrSubs('/lists'),
    showTeamBonus: showSectionOrSubs('/team-bonus'),
    showSettings: showSectionOrSubs('/settings'),
    showServices: showSectionOrSubs('/services'),
    showWork: showSectionOrSubs('/work'),
    showKitchen: showSectionOrSubs('/kitchen'),
    showNotifications: showSectionOrSubs('/notifications'),
  }), [showSectionOrSubs]);

  const {
    showFinanceOverview, showReports, showTeamAgents, showLists,
    showTeamBonus, showSettings, showServices, showWork,
    showKitchen, showNotifications,
  } = sectionVisibility;

  /* ─── Active route checks ─── */

  const isListsActive = location.pathname.startsWith('/lists');
  const isWorkActive = location.pathname.startsWith('/work');
  const isServicesActive = location.pathname.startsWith('/services');
  const isFinanceAcctActive = ['/finance/proforma', '/finance/payment-flow', '/finance/country-costs', '/finance/investments'].includes(location.pathname);
  const isTeamAgentsActive = location.pathname.startsWith('/team-agents');
  const isTeamBonusActive = location.pathname.startsWith('/team-bonus');
  const isSalesReportsActive = ['/reports', '/reports/dynamic', '/reports/chart', '/reports/agent-bonus'].includes(location.pathname);
  const isLegacyReportsActive = ['/reports/status-history', '/reports/personals', '/reports/trash-cancel', '/reports/cant-call', '/reports/paid'].includes(location.pathname);
  const isSettingsActive = location.pathname.startsWith('/settings') || location.pathname === '/duplicate-manager';
  const isNotificationsActive = location.pathname.startsWith('/notifications');
  const isKitchenActive = location.pathname.startsWith('/kitchen');

  /* ─── Accordion states ─── */

  const [listsOpen, setListsOpen] = useState(isListsActive);
  const [workOpen, setWorkOpen] = useState(isWorkActive);
  const [servicesOpen, setServicesOpen] = useState(isServicesActive);
  const [finAcctOpen, setFinAcctOpen] = useState(isFinanceAcctActive);
  const [teamAgentsOpen, setTeamAgentsOpen] = useState(isTeamAgentsActive);
  const [teamBonusOpen, setTeamBonusOpen] = useState(isTeamBonusActive);
  const [salesReportsOpen, setSalesReportsOpen] = useState(isSalesReportsActive);
  const [legacyReportsOpen, setLegacyReportsOpen] = useState(isLegacyReportsActive);
  const [settingsOpen, setSettingsOpen] = useState(isSettingsActive);
  const [notificationsOpen, setNotificationsOpen] = useState(isNotificationsActive);
  const [kitchenOpen, setKitchenOpen] = useState(isKitchenActive);

  /* ─── Filtered items ─── */

  const filteredSalesItems = useMemo(() => filterDirectItems(salesDirectItems), [filterDirectItems]);

  const workSubItems = useMemo(() => filterByPermission(workSubItemsAll), [filterByPermission]);
  const settingsSubItems = useMemo(() => filterByPermission(settingsSubItemsAll), [filterByPermission]);

  /* ─── Top-level section visibility ─── */

  const showDashboard = showSection('/dashboard');
  const showSalesSection = filteredSalesItems.length > 0 || showLists;
  const showOpsSection = showWork || showServices;
  const showFinanceSection = showFinanceOverview;
  const showReportCenter = showReports || showTeamAgents || showTeamBonus;

  /* ─── Helpers ─── */

  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const renderDirectItem = useCallback((item: NavItem) => {
    const isActive = location.pathname === item.href || 
      (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
    return (
      <NavLink
        key={item.href}
        to={item.href}
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
          isActive 
            ? "bg-sidebar-primary text-sidebar-primary-foreground" 
            : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          !isOpen && "lg:justify-center lg:px-2"
        )}
        title={!isOpen ? item.title : undefined}
      >
        <item.icon className="h-5 w-5 shrink-0" />
        {isOpen && <span>{item.title}</span>}
      </NavLink>
    );
  }, [location.pathname, isOpen]);

  const renderExpandableSection = useCallback((
    label: string,
    icon: React.ComponentType<{ className?: string }>,
    isActive: boolean,
    isOpenState: boolean,
    setOpenState: (v: boolean) => void,
    items: SubItem[],
    prependContent?: ReactNode,
  ) => {
    if (items.length === 0 && !prependContent) return null;
    const Icon = icon;
    return (
      <div>
        <button
          onClick={() => setOpenState(!isOpenState)}
          className={cn(
            "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors w-full",
            isActive
              ? "bg-sidebar-primary text-sidebar-primary-foreground"
              : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            !isOpen && "lg:justify-center lg:px-2"
          )}
          title={!isOpen ? label : undefined}
        >
          <Icon className="h-5 w-5 shrink-0" />
          {isOpen && (
            <>
              <span className="flex-1 text-left">{label}</span>
              <ChevronDown className={cn(
                "h-4 w-4 shrink-0 transition-transform duration-200",
                isOpenState && "rotate-180"
              )} />
            </>
          )}
        </button>
        {isOpenState && isOpen && (
          <div className="ml-4 mt-1 space-y-0.5 border-l border-sidebar-border pl-2">
            {prependContent}
            {items.map((sub) => {
              const subActive = location.pathname === sub.href;
              return (
                <NavLink
                  key={sub.href}
                  to={sub.href}
                  className={cn(
                    "flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs font-medium transition-colors",
                    subActive
                      ? "bg-sidebar-primary/80 text-sidebar-primary-foreground"
                      : "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  )}
                >
                  <sub.icon className="h-4 w-4 shrink-0" />
                  <span>{sub.title}</span>
                </NavLink>
              );
            })}
          </div>
        )}
      </div>
    );
  }, [isOpen, location.pathname]);

  const renderSectionLabel = useCallback((label: string) => {
    if (!isOpen) return null;
    return (
      <div className="px-3 pt-4 pb-1">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/40">{label}</span>
      </div>
    );
  }, [isOpen]);

  /* ─── Lists prepend (Lead Lists link) ─── */

  const listsLeadListsPrepend = showSection('/lists') ? (
    <NavLink
      to="/lists"
      className={cn(
        "flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs font-medium transition-colors",
        location.pathname === '/lists'
          ? "bg-sidebar-primary/80 text-sidebar-primary-foreground"
          : "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
      )}
    >
      <Users className="h-4 w-4 shrink-0" />
      <span>Lead Lists</span>
    </NavLink>
  ) : null;

  /* ═══════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════ */

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={onToggle}
        />
      )}
      
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-50 h-screen bg-sidebar-background border-r border-sidebar-border transition-all duration-300 ease-in-out flex-shrink-0",
          isOpen ? "w-64" : "w-0 lg:w-16",
          "lg:sticky lg:top-0"
        )}
      >
        <div className={cn(
          "flex flex-col h-full overflow-hidden",
          isOpen ? "w-64" : "w-0 lg:w-16"
        )}>
          {/* Header */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-sidebar-border">
            {isOpen && (
              <img 
                src={theme === 'dark' ? medius360LogoWhite : medius360LogoDark} 
                alt="Medius360" 
                className="h-8 object-contain"
              />
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggle}
              className="h-8 w-8 text-sidebar-foreground hover:bg-sidebar-accent"
            >
              {isOpen ? <ChevronLeft className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="space-y-0.5 px-2">

              {/* ══════════════ 1. DASHBOARD ══════════════ */}
              {showDashboard && (
                <>
                  {renderSectionLabel('Dashboard')}
                  {renderDirectItem({ title: 'Operations Dashboard', href: '/dashboard', icon: LayoutDashboard, allowedRoles: ['admin', 'supervisor', 'agent', 'billing', 'photographer', 'field_rep', 'appointment_setter'] })}
                </>
              )}

              {/* ══════════════ 2. SALES ══════════════ */}
              {showSalesSection && (
                <>
                  {renderSectionLabel('Sales')}
                  {filteredSalesItems.map(renderDirectItem)}
                  {showLists && renderExpandableSection(
                    'Lists', FileSpreadsheet, isListsActive, listsOpen, setListsOpen,
                    filterByPermission(listsSubItems), listsLeadListsPrepend
                  )}
                </>
              )}

              {/* ══════════════ 3. OPERATIONS ══════════════ */}
              {showOpsSection && (
                <>
                  {renderSectionLabel('Operations')}
                  
                  {showWork && renderExpandableSection(
                    'Work', Phone, isWorkActive, workOpen, setWorkOpen, workSubItems
                  )}
                  {showServices && renderExpandableSection(
                    'Services', Tag, isServicesActive, servicesOpen, setServicesOpen,
                    filterByPermission(servicesSubItems)
                  )}
                </>
              )}

              {/* ══════════════ 4. FINANCE ══════════════ */}
              {showFinanceSection && (
                <>
                  {renderSectionLabel('Finance')}

                  {/* Accounting */}
                  {showFinanceOverview && renderExpandableSection(
                    'Accounting', Receipt, isFinanceAcctActive, finAcctOpen, setFinAcctOpen,
                    filterByPermission(financeAccountingSubItems)
                  )}

                </>
              )}

              {/* ══════════════ 5. REPORT CENTER ══════════════ */}
              {showReportCenter && (
                <>
                  {renderSectionLabel('Report Center')}


                  {/* B. Team Reports */}
                  {showTeamAgents && renderExpandableSection(
                    'Team & Agents', UserCog, isTeamAgentsActive, teamAgentsOpen, setTeamAgentsOpen,
                    filterByPermission(teamAgentsSubItems)
                  )}
                  {showTeamBonus && renderExpandableSection(
                    'Team Bonus', Trophy, isTeamBonusActive, teamBonusOpen, setTeamBonusOpen,
                    filterByPermission(teamBonusSubItems)
                  )}

                  {/* C. Sales Reports */}
                  {showReports && renderExpandableSection(
                    'Sales Reports', FileText, isSalesReportsActive, salesReportsOpen, setSalesReportsOpen,
                    filterByPermission(salesReportsSubItems)
                  )}

                  {/* D. Legacy Reports */}
                  {showReports && renderExpandableSection(
                    'Other Reports', ScrollText, isLegacyReportsActive, legacyReportsOpen, setLegacyReportsOpen,
                    filterByPermission(legacyReportsSubItems)
                  )}

                </>
              )}

              {/* ══════════════ 6. SETTINGS ══════════════ */}
              {(showSettings || showNotifications) && (
                <>
                  {renderSectionLabel('Settings')}
                  {showSettings && renderExpandableSection(
                    'Settings', Settings, isSettingsActive, settingsOpen, setSettingsOpen,
                    settingsSubItems
                  )}
                  {showNotifications && renderExpandableSection(
                    'Notifications', Bell, isNotificationsActive, notificationsOpen, setNotificationsOpen,
                    filterByPermission(notificationsSubItems)
                  )}
                </>
              )}

              {/* ══════════════ KITCHEN (internal/support) ══════════════ */}
              {showKitchen && (
                <>
                  {renderSectionLabel('Kitchen')}
                  {renderExpandableSection(
                    'Kitchen', UtensilsCrossed, isKitchenActive, kitchenOpen, setKitchenOpen,
                    filterByPermission(kitchenSubItems)
                  )}
                </>
              )}

            </nav>
          </ScrollArea>

          {/* User section */}
          <div className="border-t border-sidebar-border p-4">
            {isOpen ? (
              <div className="flex items-center gap-3">
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground text-xs">
                    {user ? getInitials(user.name) : 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-sidebar-foreground truncate">
                    {user?.name}
                  </p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {user?.role}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={logout}
                  className="h-8 w-8 text-sidebar-foreground hover:bg-sidebar-accent"
                  title="Logout"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                onClick={logout}
                className="w-full h-10 text-sidebar-foreground hover:bg-sidebar-accent"
                title="Logout"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>
      </aside>
    </>
  );
});
